/**
 * CyberAgnès Chat Widget
 * Floating chatbot available on public site and admin back-office
 * Uses the CyberAgnès avatar and talks in Agnès's style
 */

import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';

const ADMIN_EMAILS = ['jbgiraud@mac.com', 'averdier@ifrap.org'];

const CyberAgnesChat = ({ variant = 'public' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { user, isAuthenticated } = useAuth();

  const isAdmin = isAuthenticated && user?.email && ADMIN_EMAILS.includes(user.email);
  const isDark = variant === 'admin';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    setPendingAction(null);

    try {
      const endpoint = isAdmin ? '/api/chat/cyberagnes/admin' : '/api/chat/cyberagnes';
      const headers = {};
      const token = localStorage.getItem('ifrap_token');
      if (token && isAdmin) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await api.post(endpoint, {
        message: text,
        session_id: sessionId,
      }, { headers });

      const data = res.data;
      if (!sessionId && data.session_id) setSessionId(data.session_id);

      // Clean response text (remove json blocks for display)
      let displayText = data.response || '';
      if (displayText.includes('```json')) {
        displayText = displayText.split('```json')[0].trim();
        const afterJson = displayText.split('```').pop();
        if (afterJson) displayText += afterJson;
      }

      setMessages(prev => [...prev, { role: 'assistant', content: displayText }]);

      if (data.action && data.needs_confirmation) {
        setPendingAction(data.action);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Désolée, un problème technique m'empêche de vous répondre. Réessayez dans un instant.",
        isError: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const confirmUpdate = async () => {
    if (!pendingAction) return;
    setLoading(true);

    try {
      const token = localStorage.getItem('ifrap_token');
      const res = await api.post('/api/chat/cyberagnes/confirm-update', {
        session_id: sessionId,
        action: pendingAction,
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const data = res.data;
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Donnée mise à jour : ${data.kpi_label} = ${data.new_value} (${data.source})`,
        isSuccess: true,
      }]);
      setPendingAction(null);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'system',
        content: err.response?.data?.detail || "Erreur lors de la mise à jour.",
        isError: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Chat bubble button
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed z-50 flex items-center gap-2 rounded-full shadow-2xl transition-all hover:scale-105 ${
          isDark
            ? 'bottom-20 right-6 bg-[#FFB000] text-black px-4 py-3'
            : 'bottom-6 right-6 bg-[#000091] text-white px-5 py-3'
        }`}
        data-testid="cyberagnes-chat-btn"
      >
        <img
          src={isDark ? '/cyberagnes-dark.jpg' : '/cyberagnes.jpg'}
          alt="CyberAgnès"
          className="w-8 h-8 rounded-full object-cover"
        />
        <span className="font-medium text-sm hidden sm:inline">CyberAgnès</span>
      </button>
    );
  }

  return (
    <div
      className={`fixed z-50 flex flex-col shadow-2xl rounded-2xl overflow-hidden transition-all ${
        isDark
          ? 'bottom-20 right-6 w-[400px] h-[540px] bg-[#0a0a1a] border border-[#FFB000]/30'
          : 'bottom-6 right-6 w-[400px] h-[560px] bg-white border border-gray-200'
      }`}
      data-testid="cyberagnes-chat-window"
    >
      {/* Header */}
      <div className={`flex items-center gap-3 px-4 py-3 ${
        isDark ? 'bg-[#FFB000]' : 'bg-gradient-to-r from-[#000091] to-[#1a1a6e]'
      }`}>
        <img
          src={isDark ? '/cyberagnes-dark.jpg' : '/cyberagnes.jpg'}
          alt="CyberAgnès"
          className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
        />
        <div className="flex-1">
          <h3 className={`font-semibold text-sm ${isDark ? 'text-black' : 'text-white'}`}>
            CyberAgnès vous répond
          </h3>
          <p className={`text-xs ${isDark ? 'text-black/60' : 'text-white/70'}`}>
            {isAdmin ? 'Mode admin — corrections activées' : 'Posez vos questions sur les finances publiques'}
          </p>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className={`p-1.5 rounded-lg transition-colors ${
            isDark ? 'hover:bg-black/10 text-black' : 'hover:bg-white/10 text-white'
          }`}
          data-testid="cyberagnes-chat-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className={`flex-1 overflow-y-auto px-4 py-3 space-y-3 ${isDark ? 'bg-[#0a0a1a]' : 'bg-gray-50'}`}>
        {/* Welcome message */}
        {messages.length === 0 && (
          <div className="text-center py-6">
            <img
              src={isDark ? '/cyberagnes-dark.jpg' : '/cyberagnes.jpg'}
              alt="CyberAgnès"
              className="w-16 h-16 rounded-full mx-auto mb-3 border-2 border-gray-200"
            />
            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Bonjour ! Je suis CyberAgnès.
            </p>
            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Posez-moi vos questions sur les finances publiques, la dette, le chômage...
            </p>
            {isAdmin && (
              <p className={`text-xs mt-2 px-3 py-1.5 rounded-lg inline-block ${
                isDark ? 'bg-[#FFB000]/20 text-[#FFB000]' : 'bg-blue-50 text-blue-600'
              }`}>
                Vous pouvez aussi me dire : "Le chômage est à 8,1%, source INSEE"
              </p>
            )}
            {/* Quick suggestions */}
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {['Quel est le déficit ?', 'État de la dette', 'Chômage actuel'].map(q => (
                <button
                  key={q}
                  onClick={() => { setInput(q); setTimeout(sendMessage, 100); }}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                    isDark
                      ? 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role !== 'user' && (
              <img
                src={isDark ? '/cyberagnes-dark.jpg' : '/cyberagnes.jpg'}
                alt=""
                className="w-7 h-7 rounded-full mr-2 mt-1 flex-shrink-0"
              />
            )}
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm ${
              msg.role === 'user'
                ? isDark ? 'bg-[#FFB000] text-black' : 'bg-[#000091] text-white'
                : msg.isSuccess
                  ? 'bg-green-100 text-green-800 border border-green-200'
                  : msg.isError
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : isDark
                      ? 'bg-[#1a1a2e] text-gray-200 border border-gray-700'
                      : 'bg-white text-gray-800 border border-gray-200 shadow-sm'
            }`}>
              {msg.isSuccess && <Check className="w-4 h-4 inline mr-1 text-green-600" />}
              {msg.isError && <AlertTriangle className="w-4 h-4 inline mr-1" />}
              <span className="whitespace-pre-wrap">{msg.content}</span>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <img src={isDark ? '/cyberagnes-dark.jpg' : '/cyberagnes.jpg'} alt="" className="w-7 h-7 rounded-full mr-2 mt-1" />
            <div className={`rounded-2xl px-4 py-3 ${isDark ? 'bg-[#1a1a2e]' : 'bg-white border border-gray-200'}`}>
              <div className="flex gap-1">
                <span className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-[#FFB000]' : 'bg-[#000091]'}`} style={{ animationDelay: '0ms' }} />
                <span className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-[#FFB000]' : 'bg-[#000091]'}`} style={{ animationDelay: '150ms' }} />
                <span className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-[#FFB000]' : 'bg-[#000091]'}`} style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {/* Pending confirmation */}
        {pendingAction && (
          <div className={`rounded-xl p-3 border ${
            isDark ? 'bg-[#FFB000]/10 border-[#FFB000]/30' : 'bg-blue-50 border-blue-200'
          }`}>
            <p className={`text-xs font-medium mb-2 ${isDark ? 'text-[#FFB000]' : 'text-blue-700'}`}>
              Confirmer la mise à jour ?
            </p>
            <p className={`text-xs mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pendingAction.confirmation_message || `${pendingAction.kpi_code} → ${pendingAction.new_value}`}
            </p>
            <div className="flex gap-2">
              <button
                onClick={confirmUpdate}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50"
                data-testid="confirm-update-btn"
              >
                <Check className="w-3.5 h-3.5" /> Confirmer
              </button>
              <button
                onClick={() => setPendingAction(null)}
                className={`px-3 py-1.5 text-xs rounded-lg ${
                  isDark ? 'bg-white/10 text-gray-300 hover:bg-white/20' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={`px-3 py-3 border-t ${isDark ? 'bg-[#0f0f20] border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Posez votre question..."
            disabled={loading}
            className={`flex-1 px-4 py-2.5 rounded-xl text-sm outline-none ${
              isDark
                ? 'bg-[#1a1a2e] text-white border border-gray-700 placeholder-gray-500 focus:border-[#FFB000]'
                : 'bg-gray-100 text-gray-900 border border-gray-200 placeholder-gray-400 focus:border-[#000091]'
            }`}
            data-testid="cyberagnes-chat-input"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className={`p-2.5 rounded-xl transition-colors disabled:opacity-30 ${
              isDark
                ? 'bg-[#FFB000] text-black hover:bg-[#FFB000]/80'
                : 'bg-[#000091] text-white hover:bg-[#000091]/80'
            }`}
            data-testid="cyberagnes-chat-send"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CyberAgnesChat;
